import { createAsyncThunk } from "@reduxjs/toolkit";

import { alertActions } from "../alert/alertSlice";

import {
    createSponsor as createSponsorFromAPI,
    deleteSponsor as deleteSponsorFromAPI,
    getAgeLevels as getAgeLevelsFromAPI,
    getFieldsList,
    getPhotos as getPhotosFromAPI,
    getPositions as getPositionsFromAPI,
    getSeasons as getSeasonsFromAPI,
    getSiteSetting,
    getSponsors as getSponsorsFromAPI,
    getUmpires as getUmpiresFromAPI,
    updateSponsor as updateSponsorFromAPI,
} from "../../lib/api";
import {
    IAPIResponse,
    IAgeLevel,
    IField,
    IPhoto,
    IPosition,
    ISeason,
    ISiteSetting,
    ISponsor,
    IUser,
} from "../../types/api";

interface IGetAgeLevelsResponse extends IAPIResponse {
    ageLevels: IAgeLevel[];
}

interface IGetUmpiresResponse extends IAPIResponse {
    umpires: Array<Omit<IUser, "password" | "meta"> & { name: string }>;
}

export const getAgeLevels = createAsyncThunk<
    IGetAgeLevelsResponse,
    void,
    { rejectValue: string }
>("global/getAgeLevels", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getAgeLevelsFromAPI();

        return response;
    } catch (e) {
        const error = e as string;
        // dispatch(
        //     alertActions.error({
        //         level: "danger",
        //         text: "Age Level retrieval failed. Please try again later or contact an admin",
        //         title: error,
        //     }),
        // );
        return rejectWithValue(error);
    }
});

export const getUmpires = createAsyncThunk<
    IGetUmpiresResponse,
    void,
    { rejectValue: string }
>("global/getUmpires", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getUmpiresFromAPI();

        return response;
    } catch (e) {
        const error = e as string;
        // dispatch(
        //     alertActions.error({
        //         level: "danger",
        //         text: "Umpire retrieval failed. Please try again later or contact an admin",
        //         title: error,
        //     }),
        // );
        return rejectWithValue(error);
    }
});

export const getSettings = createAsyncThunk<
    ISiteSetting[],
    void,
    { rejectValue: string }
>("global/getSettings", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getSiteSetting();

        const settings = Array.isArray(response.setting)
            ? response.setting
            : [response.setting];

        return settings;
    } catch (e) {
        const error = e as string;
        // dispatch(
        //     alertActions.error({
        //         level: "danger",
        //         text: "Settings retrieval failed. Please try again later or contact an admin",
        //         title: error,
        //     }),
        // );
        return rejectWithValue(error);
    }
});

export const getFields = createAsyncThunk<
    IField[],
    void,
    { rejectValue: string }
>("global/getFields", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getFieldsList();

        return response.fields;
    } catch (e) {
        const error = e as string;
        // dispatch(
        //     alertActions.error({
        //         level: "danger",
        //         text: "Settings retrieval failed. Please try again later or contact an admin",
        //         title: error,
        //     }),
        // );
        return rejectWithValue(error);
    }
});

export const getSeasons = createAsyncThunk<
    ISeason[],
    void,
    { rejectValue: string }
>("global/getSeasons", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getSeasonsFromAPI();

        return response.seasons;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getPositions = createAsyncThunk<
    IPosition[],
    void,
    { rejectValue: string }
>("global/getPositions", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getPositionsFromAPI();

        return response.positions;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getPhotos = createAsyncThunk<
    IPhoto[],
    void,
    { rejectValue: string }
>("global/getPhotos", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getPhotosFromAPI();

        return response.photos;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const getSponsors = createAsyncThunk<
    ISponsor[],
    void,
    { rejectValue: string }
>("global/getSponsors", async (_, { rejectWithValue, dispatch }) => {
    try {
        const response = await getSponsorsFromAPI();

        return response.sponsors;
    } catch (e) {
        const error = e as string;
        return rejectWithValue(error);
    }
});

export const updateSponsor = createAsyncThunk<
    ISponsor,
    {
        sponsorId: ISponsor["id"];
        name: ISponsor["name"];
        link?: ISponsor["link"];
        logo?: File[];
    },
    { rejectValue: string }
>(
    "global/updateSponsor",
    async ({ sponsorId, name, link, logo }, { rejectWithValue, dispatch }) => {
        try {
            const response = await updateSponsorFromAPI(
                sponsorId,
                name,
                link,
                logo,
            );

            dispatch(
                alertActions.success({
                    level: "success",
                    title: response.message,
                    dismissible: true,
                }),
            );

            return response.sponsor;
        } catch (error: any) {
            dispatch(
                alertActions.error({
                    level: "danger",
                    title: "Updating the sponsor failed with the following error:",
                    text:
                        error +
                        "\n\nIf the problem persists, please contact the site admin.",
                    dismissible: true,
                }),
            );
            return rejectWithValue(error);
        }
    },
);

export const createSponsor = createAsyncThunk<
    ISponsor,
    {
        name: ISponsor["name"];
        link?: ISponsor["link"];
        logo?: File[];
    },
    { rejectValue: string }
>(
    "global/createSponsor",
    async ({ name, link, logo }, { rejectWithValue, dispatch }) => {
        try {
            const response = await createSponsorFromAPI(name, link, logo);
            dispatch(
                alertActions.success({
                    level: "success",
                    title: response.message,
                    dismissible: true,
                    persist: true,
                }),
            );
            return response.sponsor;
        } catch (error: any) {
            dispatch(
                alertActions.error({
                    level: "danger",
                    title: "Creating the sponsor failed with the following error:",
                    text:
                        error +
                        "\n\nIf the problem persists, please contact the site admin.",
                    dismissible: true,
                }),
            );
            return rejectWithValue(error);
        }
    },
);

export const deleteSponsor = createAsyncThunk<
    number,
    {
        id: ISponsor["id"];
    },
    { rejectValue: string }
>("global/deleteSponsor", async ({ id }, { rejectWithValue, dispatch }) => {
    try {
        const response = await deleteSponsorFromAPI(id);
        dispatch(
            alertActions.success({
                level: "success",
                title: response.message,
                dismissible: true,
                persist: true,
            }),
        );
        return id;
    } catch (error: any) {
        dispatch(
            alertActions.error({
                level: "danger",
                title: "Deleting the sponsor failed with the following error:",
                text:
                    error +
                    "\n\nIf the problem persists, please contact the site admin.",
                dismissible: true,
            }),
        );
        return rejectWithValue(error);
    }
});
